import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";

import WSTextField from "../../../Components/TextBox/WSTextBox";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useNavigate, useParams } from "react-router-dom";
import { callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";

const Brand = () => {
   const [loading, setLoading] = useState(false);
   const [isEditMode, setIsEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | number>(0);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const { showMessage } = useSnackbar();
    const params = useParams();
    const id = params.id;
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
      } = useForm({
        mode: "onChange",
        defaultValues: {
          BrandName: "",
          Description: "",
          IsActive: true,
        },
      });

      useEffect(() => {
        if (!id || id === "undefined" || id === "null" || id.trim() === "") {
          setIsEditMode(false);
          reset();
          return;
        }

        setIsEditMode(true);
        fetchBrandData(id);
      }, [id, showMessage, reset]);


      const fetchBrandData = async (brandId: string) => {
          try {
            setLoading(true);

            const response = await callAPI(`brands/${brandId}`, Method.GET);

            if (response?.success && response.data) {
              const brandData = response.data;

              reset({
                BrandName: brandData.BrandName || "",
                Description: brandData.Description || "",
                IsActive: brandData.IsActive !== false,
              });
            } else {
              showMessage(response?.message || `Brand ${brandId} not found`, "error");
              setIsEditMode(false);
            }
          } catch (error: any) {
            showMessage("Failed to load brand data", "error");
            setIsEditMode(false);
          } finally {
            setLoading(false);
          }
        };


         const onSubmit = async (data: any) => {
            try {
              setLoading(true);
              const requestBody = {
                BrandName: data.BrandName?.trim(),
                Description: data.Description || "",
                IsActive: data.IsActive || true,
              };

              let response;
              if (isEditMode && id) {
                response = await callAPI(`brands/${id}`, Method.PUT, requestBody);
              } else {
                response = await callAPI("brands/create", Method.POST, requestBody);
              }

              if (response?.success) {
                showMessage(
                  isEditMode
                    ? "Brand updated successfully!"
                    : "Brand created successfully!",
                  "success"
                );
                navigate("/brand/list");
              } else {
                showMessage(response?.message || "Operation failed", "error");
              }
            } catch (error: any) {
              showMessage(error?.message || "Operation failed", "error");
            } finally {
              setLoading(false);
            }
          };

          const handleDeleteClick = () => {
            if (!id) return;
            setSelectedId(id);
            setPopupOpen(true);
          };

          const handleBack = () => {
            navigate("/brand/list");
          };

  return (
    <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
      {}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
            {isEditMode ? "Edit Brand" : "Brand Management"}
          </Typography>
        </Box>

        <Box>
          {isEditMode && id && (
            <IconButton
              onClick={handleDeleteClick}
              disabled={loading}
              sx={{
                background: "black",
                color: "#fff",
                "&:hover": { background: "black" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      {}
      <Paper
        sx={{
          borderRadius: "16px",
          p: 3,
          background: "#fafafa",
          position: "relative",
        }}
      >

        {}
        <Box display="flex" justifyContent="space-between" mb={7}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            Brand Details
          </Typography>
          <Tooltip title={isEditMode ? "Update Brand" : "Create Brand"} arrow>
                     <IconButton
                       sx={{ background: "#000", color: "#fff" }}
                       onClick={handleSubmit(onSubmit)}
                       disabled={!isValid || loading}
                     >
                       <SaveIcon fontSize="small" />
                     </IconButton>
                   </Tooltip>
        </Box>

        <form noValidate>
         <Grid container rowSpacing={8} columnSpacing={5}>

          <Grid item xs={3}>
            <WSTextField
              name="BrandName"
              label="Brand Name"
              id="BrandName"
              control={control}
            />
          </Grid>

          <Grid item xs={8}>
            <WSTextField
              name="Description"
              label="Description"
              id="Description"
              control={control}
            />
          </Grid>

        </Grid>
        </form>
      </Paper>

      {}
      <ConfirmationPopup
        type="DELETE"
        apiUrl="brands"
        id={selectedId}
        confirmMessage="Are you sure you want to delete this Brand?"
        open={isPopupOpen}
        onClose={() => setPopupOpen(false)}
        onConfirm={async (data) => {
          if (!selectedId) return;

          if (data?.success === true) {
            showMessage("Brand deleted successfully!", "success");
            navigate("/brand/list");
          } else {
            showMessage(data?.detail, "error");
          }

          setPopupOpen(false);
        }}
      />
    </Box>
  );
};

export default Brand;
